import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { GripVertical, Pencil, Check, X } from "lucide-react";
import { useRequireAuth } from "../../hooks/useRequireAuth";
import { useVenueName } from "../../hooks/useVenueName";

export default function NavigationEditor({ venue: propVenue }) {
  useRequireAuth();

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const { name } = useVenueName();
  const venue = propVenue || name;

  // Stany do edycji displayname z /config/app
  const [displayName, setDisplayName] = useState("");
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [displayNameInput, setDisplayNameInput] = useState("");

  // Drag & drop + edycja stron
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    if (!venue) return;

    const loadData = async () => {
      try {
        // 1. Ładujemy displayname z /config/app
        const configRef = doc(db, "config", "app");
        const configSnap = await getDoc(configRef);

        let loadedName = "Nazwa aplikacji";
        if (configSnap.exists()) {
          loadedName = configSnap.data()?.displayname || loadedName;
        }
        setDisplayName(loadedName);
        setDisplayNameInput(loadedName);

        // 2. Ładujemy strony
        const pagesSnap = await getDocs(collection(db, "venues", venue, "pages"));
        const list = pagesSnap.docs
          .map((d) => ({
            id: d.id,
            title: d.data().title || d.id,
            order: d.data().order ?? 999,
            slug: d.data().slug || "",
          }))
          .sort((a, b) => a.order - b.order);

        setPages(list);
      } catch (err) {
        console.error("Błąd ładowania danych:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [venue]);

  // ── Edycja displayname ───────────────────────────────────────────────
  const startEditDisplayName = () => {
    setDisplayNameInput(displayName);
    setEditingDisplayName(true);
  };

  const cancelEditDisplayName = () => {
    setEditingDisplayName(false);
  };

  const saveDisplayName = async () => {
    const trimmed = displayNameInput.trim();
    if (!trimmed) {
      alert("Nazwa nie może być pusta");
      return;
    }

    setSaving(true);
    try {
      const configRef = doc(db, "config", "app");
      await updateDoc(configRef, {
        displayname: trimmed,
      });

      setDisplayName(trimmed);
      setEditingDisplayName(false);
    } catch (err) {
      console.error("Błąd zapisu displayname:", err);
      alert("Nie udało się zapisać nazwy");
    } finally {
      setSaving(false);
    }
  };

  // ── Edycja tytułu strony ─────────────────────────────────────────────
  const startEditing = (page) => {
    setEditingId(page.id);
    setEditValue(page.title);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveEdit = async (pageId) => {
    if (!editValue.trim()) {
      alert("Nazwa nie może być pusta");
      return;
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, "venues", venue, "pages", pageId), {
        title: editValue.trim(),
      });

      setPages((prev) =>
        prev.map((p) =>
          p.id === pageId ? { ...p, title: editValue.trim() } : p
        )
      );
      setEditingId(null);
      setEditValue("");
    } catch (err) {
      console.error("Błąd zapisu nazwy strony:", err);
      alert("Nie udało się zapisać");
    } finally {
      setSaving(false);
    }
  };

  // ── Przesuwanie kolejności ───────────────────────────────────────────
  const move = (index, dir) => {
    const copy = [...pages];
    const target = index + dir;
    if (target < 0 || target >= copy.length) return;
    [copy[index], copy[target]] = [copy[target], copy[index]];
    setPages(copy.map((p, i) => ({ ...p, order: i })));
  };

  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }

    const copy = [...pages];
    const draggedItem = copy[dragIndex];
    copy.splice(dragIndex, 1);
    copy.splice(dropIndex, 0, draggedItem);

    setPages(copy.map((p, i) => ({ ...p, order: i })));
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      await Promise.all(
        pages.map((p, i) =>
          updateDoc(doc(db, "venues", venue, "pages", p.id), { order: i })
        )
      );
    } catch (err) {
      console.error("Błąd zapisywania kolejności:", err);
      alert("Problem z zapisaniem kolejności");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="mt-20 text-center">Ładowanie...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto mt-20 space-y-8 pb-12">
      {/* Edycja głównej nazwy aplikacji */}
      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
        <h3 className="text-xl font-medium mb-4 text-center md:text-left">
          Nazwa wyświetlana w aplikacji
        </h3>

        {editingDisplayName ? (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <input
              autoFocus
              value={displayNameInput}
              onChange={(e) => setDisplayNameInput(e.target.value)}
              className="flex-1 border border-gray-300 rounded px-4 py-3 text-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Wpisz nazwę..."
            />
            <div className="flex gap-3 justify-center sm:justify-start">
              <button
                onClick={saveDisplayName}
                disabled={saving}
                className="text-emerald-600 hover:text-emerald-800 p-2"
                title="Zapisz"
              >
                <Check size={32} />
              </button>
              <button
                onClick={cancelEditDisplayName}
                className="text-red-600 hover:text-red-800 p-2"
                title="Anuluj"
              >
                <X size={32} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-2xl md:text-3xl font-semibold">
              {displayName || <span className="text-gray-400">— brak nazwy —</span>}
            </div>
            <button
              onClick={startEditDisplayName}
              className="text-gray-500 hover:text-emerald-600 transition-colors"
              title="Edytuj nazwę główną"
            >
              <Pencil size={28} />
            </button>
          </div>
        )}
      </div>

      <h2 className="text-2xl font-semibold text-center">
        Edytor menu – kolejność i nazwy stron
      </h2>

      {/* Podgląd nawigacji */}
      <div className="bg-white shadow-xl rounded-lg overflow-hidden">
        {/* Desktop */}
{/* Desktop – scrollowalna poziomo lista */}
<div className="hidden md:block border-b">
  <div className="px-6 py-4 overflow-x-auto">
    <ul className="inline-flex items-center gap-6 font-medium text-lg whitespace-nowrap">
      {pages.map((page, i) => (
        <li
          key={page.id}
          draggable
          onDragStart={(e) => handleDragStart(e, i)}
          onDragOver={(e) => handleDragOver(e, i)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, i)}
          onDragEnd={handleDragEnd}
          className={`flex items-center gap-3 py-2 px-4 rounded group cursor-move transition-all min-w-[140px] ${
            dragIndex === i ? "opacity-50 scale-105" : ""
          } ${dragOverIndex === i ? "scale-105 border-2 border-emerald-400 bg-emerald-50" : "bg-white"}`}
        >
          <GripVertical className="text-gray-500 flex-shrink-0" />
          {editingId === page.id ? (
            <div className="flex items-center gap-2">
              <input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 min-w-[120px] max-w-[220px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveEdit(page.id);
                  if (e.key === "Escape") cancelEdit();
                }}
              />
              <button
                onClick={() => saveEdit(page.id)}
                disabled={saving}
                className="text-emerald-600 hover:text-emerald-800"
              >
                <Check size={18} />
              </button>
              <button
                onClick={cancelEdit}
                className="text-red-600 hover:text-red-800"
              >
                <X size={18} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-gray-700">{page.title}</span>
              <button
                onClick={() => startEditing(page)}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:text-emerald-600"
              >
                <Pencil size={16} />
              </button>
            </div>
          )}

          <div className="flex flex-col ml-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => move(i, -1)}
              className="text-xs text-gray-500 hover:text-gray-900 leading-none"
            >
              ↑
            </button>
            <button
              onClick={() => move(i, 1)}
              className="text-xs text-gray-500 hover:text-gray-900 leading-none"
            >
              ↓
            </button>
          </div>
        </li>
      ))}
    </ul>
  </div>
</div>

        {/* Mobile */}
        <div className="md:hidden px-6 pt-10 pb-20 bg-white min-h-screen">
          <ul className="flex flex-col items-center gap-8 font-medium text-2xl">
            {pages.map((page, i) => (
              <li
                key={page.id}
                draggable
                onDragStart={(e) => handleDragStart(e, i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, i)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-5 group cursor-move w-full max-w-md ${
                  dragIndex === i ? "opacity-60" : ""
                }`}
              >
                <GripVertical className="text-gray-500 text-4xl flex-shrink-0" />

                {editingId === page.id ? (
                  <div className="flex-1 flex items-center gap-3">
                    <input
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="flex-1 border border-gray-300 rounded px-3 py-2 text-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      autoFocus
                    />
                    <button onClick={() => saveEdit(page.id)} className="text-emerald-600">
                      <Check size={28} />
                    </button>
                    <button onClick={cancelEdit} className="text-red-600">
                      <X size={28} />
                    </button>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-between">
                    <span>{page.title}</span>
                    <button onClick={() => startEditing(page)} className="text-gray-500">
                      <Pencil size={24} />
                    </button>
                  </div>
                )}

                <div className="flex flex-col">
                  <button onClick={() => move(i, -1)} className="text-3xl">↑</button>
                  <button onClick={() => move(i, 1)} className="text-3xl">↓</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Przyciski akcji */}
      <div className="flex flex-col items-center gap-6 mt-10">
        <p className="text-sm text-gray-600 text-center">
          Przeciągaj za <GripVertical className="inline w-5 h-5 mx-1" /> lub używaj strzałek ↑↓ • 
          Kliknij <Pencil className="inline w-5 h-5 mx-1" /> aby zmienić nazwę wyświetlaną w menu
        </p>

        <div className="flex flex-col sm:flex-row gap-6">
          <button
            onClick={saveOrder}
            disabled={saving || pages.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white px-10 py-4 rounded-lg font-semibold transition-colors"
          >
            {saving ? "Zapisywanie…" : "Zapisz kolejność"}
          </button>

          {saving && (
            <button
              onClick={() => window.location.reload()}
              className="text-gray-600 hover:text-gray-900 underline"
            >
              Odśwież stronę
            </button>
          )}
        </div>

        <a
          href={`/admin`}
          className="text-emerald-600 hover:text-emerald-800 font-medium"
        >
          Wróć do panelu administracyjnego
        </a>
      </div>
    </div>
  );
}